import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useConfig } from '../context/ConfigContext';

// Tier definitions
const TIERS = {
  STARTER: {
    name: 'Starter',
    lockedFeatures: ['enableEbayIntegration', 'enableTaxEscrow', 'enableAuctionCalendar'],
    upgradeMessage: 'Upgrade to PRO'
  },
  PRO: {
    name: 'Pro',
    lockedFeatures: [],
    upgradeMessage: null
  },
  ENTERPRISE: {
    name: 'Enterprise',
    lockedFeatures: [],
    extraFeatures: ['enableApiAccess'],
    upgradeMessage: null
  }
};

// PRO tier features (grouped at top)
const PRO_FEATURES = [
  {
    key: 'enableEbayIntegration',
    label: 'eBay Integration',
    description: 'Enable eBay listing generator and auction winner management',
    requiredTier: 'PRO'
  },
  {
    key: 'enableTaxEscrow',
    label: 'Tax Escrow',
    description: 'Collect monthly tax and HOA escrow with each payment. When this is off, new loans are created with no escrow and their buyers pay principal and interest only. Loans that already exist keep the escrow their signed contract states, and only the Adjust Escrow action can change it.',
    requiredTier: 'PRO'
  },
  {
    key: 'enableAuctionCalendar',
    label: 'Auction Calendar',
    description: 'Manage upcoming auction dates and property opportunities',
    requiredTier: 'PRO'
  }
];

// Standard tier features
const STANDARD_FEATURES = [
  {
    key: 'enableHoaTracking',
    label: 'HOA Tracking',
    description: 'Track and manage HOA fees for properties with homeowner associations',
    requiredTier: 'STARTER'
  },
  {
    key: 'enablePropertyCovenants',
    label: 'Property Covenants',
    description: 'Display covenant and restriction information on property listings',
    requiredTier: 'STARTER'
  },
  {
    key: 'enablePropertySources',
    label: 'Property Sources',
    description: 'Track acquisition sources, auction sites, and login credentials',
    requiredTier: 'STARTER'
  },
  {
    key: 'enableSoldShowcase',
    label: 'Sold Properties Showcase',
    description: 'Show sold properties page to build trust with potential buyers',
    requiredTier: 'STARTER'
  },
  {
    key: 'enableEscrowAlerts',
    label: 'Escrow Alerts',
    description: 'Email a daily digest when a tax installment is approaching with nothing remitted against it, when one is already past due, or when a loan holds less escrow than its next installment needs. Nothing is sent on days when there is nothing to report.',
    requiredTier: 'STARTER'
  }
];

// All features combined for counting
const FEATURE_TOGGLES = [...PRO_FEATURES, ...STANDARD_FEATURES];

// Enterprise-only features
const ENTERPRISE_FEATURES = [
  {
    key: 'enableApiAccess',
    label: 'API Access',
    description: 'Enable REST API access for third-party integrations',
    requiredTier: 'ENTERPRISE'
  }
];

// Payment provider options
const PAYMENT_PROVIDERS = [
  { value: 'square', label: 'Square', description: 'Square payment processing' },
  { value: 'stripe', label: 'Stripe', description: 'Stripe payment processing' },
  { value: 'both', label: 'Both', description: 'Allow customers to choose' }
];

function FeatureSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasCustomSettings, setHasCustomSettings] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Current subscription tier (will come from API in future)
  // eslint-disable-next-line no-unused-vars
  const [currentTier, setCurrentTier] = useState('STARTER');

  // Helper to check if a feature is locked for current tier
  const isFeatureLocked = (featureKey) => {
    return TIERS[currentTier]?.lockedFeatures?.includes(featureKey) || false;
  };

  // Helper to check if feature is enterprise-only (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const isEnterpriseFeature = (featureKey) => {
    return ENTERPRISE_FEATURES.some(f => f.key === featureKey);
  };

  // Default features from server config (reserved for reset functionality)
  // eslint-disable-next-line no-unused-vars
  const [defaults, setDefaults] = useState({});

  // Current feature settings
  const [features, setFeatures] = useState({
    enableEbayIntegration: true,
    enableHoaTracking: true,
    enableTaxEscrow: true,
    enablePropertyCovenants: true,
    enablePropertySources: true,
    enableAuctionCalendar: true,
    enableSoldShowcase: true
  });

  // Payment provider setting
  const [paymentProvider, setPaymentProvider] = useState('square');
  const [originalPaymentProvider, setOriginalPaymentProvider] = useState('square');

  // Original features to track changes
  const [originalFeatures, setOriginalFeatures] = useState({});

  useEffect(() => {
    fetchFeatureSettings();
  }, []);

  const fetchFeatureSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      // Fetch feature settings and billing status in parallel
      const [featuresResponse, billingResponse] = await Promise.all([
        api.get('/admin/settings/features', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/billing/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).catch(() => null)
      ]);

      setFeatures(featuresResponse.data.features);
      setOriginalFeatures(featuresResponse.data.features);
      setDefaults(featuresResponse.data.defaults);
      setHasCustomSettings(featuresResponse.data.hasCustomSettings);

      // Load payment provider setting
      if (featuresResponse.data.paymentProvider) {
        setPaymentProvider(featuresResponse.data.paymentProvider);
        setOriginalPaymentProvider(featuresResponse.data.paymentProvider);
      }

      // Set tier from billing status
      if (billingResponse?.tier) {
        const tierMap = {
          'free_trial': 'STARTER',
          'starter': 'STARTER',
          'pro': 'PRO',
          'enterprise': 'ENTERPRISE'
        };
        setCurrentTier(tierMap[billingResponse.tier] || 'PRO');
      }

      setError('');
    } catch (err) {
      console.error('Failed to fetch feature settings:', err);
      setError('Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    // Don't allow toggling locked features
    if (isFeatureLocked(key)) {
      return;
    }
    const newFeatures = { ...features, [key]: !features[key] };
    setFeatures(newFeatures);
    checkForChanges(newFeatures, paymentProvider);
  };

  const handlePaymentProviderChange = (value) => {
    setPaymentProvider(value);
    checkForChanges(features, value);
  };

  const checkForChanges = (newFeatures, newProvider) => {
    const featuresChanged = JSON.stringify(newFeatures) !== JSON.stringify(originalFeatures);
    const providerChanged = newProvider !== originalPaymentProvider;
    setHasChanges(featuresChanged || providerChanged);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/settings/features',
        { features, paymentProvider },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalFeatures(features);
      setOriginalPaymentProvider(paymentProvider);
      setHasChanges(false);
      setHasCustomSettings(true);
      setSuccess('Feature settings saved successfully! Changes will take effect on next page load.');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to save feature settings:', err);
      setError(err.response?.data?.error || 'Failed to save feature settings');
      // Revert to original on error
      setFeatures(originalFeatures);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all features to defaults? This will affect all users.')) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.delete('/admin/settings/features', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFeatures(response.data.features);
      setOriginalFeatures(response.data.features);
      setPaymentProvider(response.data.paymentProvider || 'square');
      setOriginalPaymentProvider(response.data.paymentProvider || 'square');
      setHasChanges(false);
      setHasCustomSettings(false);
      setSuccess('Features reset to defaults successfully!');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to reset feature settings:', err);
      setError('Failed to reset feature settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setFeatures(originalFeatures);
    }
    navigate('/admin/settings-hub');
  };

  // Count enabled features
  const enabledCount = FEATURE_TOGGLES.filter(f => features[f.key]).length;

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-header-text">
            <h1 className="admin-page-title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button onClick={handleCancel} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Feature Settings</h1>
        <p className="admin-hub-subtitle">Enable or disable platform features</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          backgroundColor: 'var(--color-success-bg)',
          border: '1px solid var(--color-success-border)',
          color: 'var(--color-success-text)',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '20px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>✓</span> {success}
        </div>
      )}

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>

        {/* Feature Toggles */}
        <div className="card" style={{ padding: '25px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '10px', color: 'var(--color-brand-text)' }}>
            Platform Features
          </h2>
          <p style={{ color: 'var(--color-text-light)', marginBottom: '20px', fontSize: '14px' }}>
            Toggle features on or off to customize your platform. Disabled features will be hidden from admin menus and customer-facing pages.
          </p>

          {hasCustomSettings && (
            <div style={{
              backgroundColor: 'var(--color-success-bg)',
              padding: '10px 15px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px',
              color: 'var(--color-success-text)'
            }}>
              Custom feature settings are currently active
            </div>
          )}

          {/* PRO Features Section */}
          <div style={{
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: 'var(--color-warning-bg)',
            borderRadius: '12px',
            border: '2px solid var(--color-warning-border)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ fontSize: '20px' }}>⭐</span>
              <h3 style={{ margin: 0, color: 'var(--color-warning-text)', fontSize: '18px' }}>PRO Features</h3>
              <span style={{
                fontSize: '11px',
                backgroundColor: '#f59e0b',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '12px',
                fontWeight: '600'
              }}>PREMIUM</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {PRO_FEATURES.map(feature => {
                const locked = isFeatureLocked(feature.key);
                return (
                  <div
                    key={feature.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      backgroundColor: locked ? '#fef3c7' : (features[feature.key] ? '#fef9c3' : '#fffbeb'),
                      borderRadius: '8px',
                      border: locked ? '1px solid #fcd34d' : (features[feature.key] ? '2px solid #eab308' : '1px solid #fcd34d'),
                      transition: 'all 0.2s ease',
                      opacity: locked ? 0.8 : 1
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        color: locked ? '#92400e' : (features[feature.key] ? '#713f12' : '#a16207'),
                        marginBottom: '4px',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {locked && <span style={{ fontSize: '14px' }}>🔒</span>}
                        {feature.label}
                        <span style={{
                          fontSize: '9px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>PRO</span>
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: locked ? '#b45309' : (features[feature.key] ? '#854d0e' : '#a16207')
                      }}>
                        {feature.description}
                      </div>
                      {locked && (
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--color-warning-text)',
                          marginTop: '6px',
                          fontWeight: '600'
                        }}>
                          {TIERS[currentTier]?.upgradeMessage || 'Upgrade to unlock'}
                        </div>
                      )}
                    </div>
                    <div
                      onClick={() => handleToggle(feature.key)}
                      style={{
                        width: '56px',
                        height: '28px',
                        backgroundColor: locked ? '#fcd34d' : (features[feature.key] ? '#eab308' : '#d4d4d4'),
                        borderRadius: '14px',
                        position: 'relative',
                        cursor: locked ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        marginLeft: '20px',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: locked ? '2px' : (features[feature.key] ? '30px' : '2px'),
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Standard Features Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', color: 'var(--color-text-light)', fontSize: '16px' }}>
              Standard Features
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {STANDARD_FEATURES.map(feature => {
                const locked = isFeatureLocked(feature.key);
                return (
                  <div
                    key={feature.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      backgroundColor: locked ? '#f5f5f5' : (features[feature.key] ? '#f8fff8' : '#f8f9fa'),
                      borderRadius: '8px',
                      border: locked ? '1px solid #ddd' : (features[feature.key] ? '1px solid #c3e6c3' : '1px solid #e9ecef'),
                      transition: 'all 0.2s ease',
                      opacity: locked ? 0.7 : 1
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        color: locked ? '#999' : (features[feature.key] ? '#333' : '#888'),
                        marginBottom: '4px',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {locked && <span style={{ fontSize: '14px' }}>🔒</span>}
                        {feature.label}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: locked ? '#aaa' : (features[feature.key] ? '#666' : '#999')
                      }}>
                        {feature.description}
                      </div>
                    </div>
                    <div
                      onClick={() => handleToggle(feature.key)}
                      style={{
                        width: '56px',
                        height: '28px',
                        backgroundColor: locked ? '#ddd' : (features[feature.key] ? 'var(--color-primary, #2e7d32)' : '#ccc'),
                        borderRadius: '14px',
                        position: 'relative',
                        cursor: locked ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        marginLeft: '20px',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: locked ? '2px' : (features[feature.key] ? '30px' : '2px'),
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Enterprise-only features */}
          {currentTier === 'ENTERPRISE' && ENTERPRISE_FEATURES.map(feature => (
              <div
                key={feature.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  backgroundColor: features[feature.key] ? '#f8fff8' : '#f8f9fa',
                  borderRadius: '8px',
                  border: features[feature.key] ? '1px solid #c3e6c3' : '1px solid #e9ecef',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    color: features[feature.key] ? '#333' : '#888',
                    marginBottom: '4px',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {feature.label}
                    <span style={{
                      fontSize: '10px',
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontWeight: '500'
                    }}>ENTERPRISE</span>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: features[feature.key] ? '#666' : '#999'
                  }}>
                    {feature.description}
                  </div>
                </div>

                {/* Toggle Switch */}
                <div
                  onClick={() => handleToggle(feature.key)}
                  style={{
                    width: '56px',
                    height: '28px',
                    backgroundColor: features[feature.key] ? 'var(--color-primary, #2e7d32)' : '#ccc',
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    marginLeft: '20px',
                    flexShrink: 0
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: features[feature.key] ? '30px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </div>
            ))}

          {/* Payment Provider Section */}
          <div style={{
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid var(--color-border)'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-brand-text)' }}>
              Payment Processing
            </h3>
            <p style={{ color: 'var(--color-text-light)', marginBottom: '15px', fontSize: '14px' }}>
              Select which payment processor to use for online payments.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {PAYMENT_PROVIDERS.map(provider => (
                <label
                  key={provider.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: paymentProvider === provider.value ? '#f0f8ff' : '#f8f9fa',
                    borderRadius: '8px',
                    border: paymentProvider === provider.value ? '2px solid var(--color-primary)' : '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="radio"
                    name="paymentProvider"
                    value={provider.value}
                    checked={paymentProvider === provider.value}
                    onChange={() => handlePaymentProviderChange(provider.value)}
                    style={{ marginRight: '12px', width: '18px', height: '18px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>{provider.label}</div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>{provider.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '10px' }}>
              Note: Requires API keys configured in server environment variables.
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid var(--color-border)'
          }}>
            <button
              onClick={() => !demoDisabled && handleSave()}
              disabled={!hasChanges || saving || demoDisabled}
              className="btn btn-primary"
              style={{
                opacity: (!hasChanges || saving || demoDisabled) ? 0.6 : 1,
                cursor: (!hasChanges || saving || demoDisabled) ? 'not-allowed' : 'pointer'
              }}
              title={demoDisabled ? 'Disabled in demo mode' : ''}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={() => !demoDisabled && handleReset()}
              disabled={saving || !hasCustomSettings || demoDisabled}
              className="btn btn-secondary"
              style={{
                opacity: (saving || !hasCustomSettings || demoDisabled) ? 0.6 : 1,
                cursor: (saving || !hasCustomSettings || demoDisabled) ? 'not-allowed' : 'pointer'
              }}
              title={demoDisabled ? 'Disabled in demo mode' : ''}
            >
              Reset to Defaults
            </button>
          </div>
        </div>

        {/* Info Panel */}
        <div>
          {/* Current Tier Card */}
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-brand-text)' }}>
              Current Plan
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px'
            }}>
              <span style={{ color: 'var(--color-text-light)' }}>Subscription Tier:</span>
              <span style={{
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '4px 12px',
                borderRadius: '6px',
                backgroundColor: currentTier === 'STARTER' ? '#e0e7ff' : currentTier === 'PRO' ? '#6366f1' : '#7c3aed',
                color: currentTier === 'STARTER' ? '#4338ca' : 'white'
              }}>
                {TIERS[currentTier]?.name || currentTier}
              </span>
            </div>
            {currentTier === 'STARTER' && (
              <div style={{
                backgroundColor: 'var(--color-warning-bg)',
                padding: '10px 12px',
                borderRadius: '6px',
                marginTop: '10px',
                fontSize: '13px',
                color: 'var(--color-warning-text)'
              }}>
                <strong>3 features locked</strong> - Upgrade to PRO to unlock eBay Integration, Tax Escrow, and Auction Calendar.
              </div>
            )}
          </div>

          <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-brand-text)' }}>
              Summary
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px'
            }}>
              <span style={{ color: 'var(--color-text-light)' }}>Features Enabled:</span>
              <span style={{
                fontWeight: 'bold',
                color: 'var(--color-brand-text)',
                fontSize: '18px'
              }}>
                {enabledCount} / {FEATURE_TOGGLES.length}
              </span>
            </div>
            <div style={{
              backgroundColor: 'var(--color-surface-sunken)',
              borderRadius: '8px',
              padding: '3px',
              marginTop: '10px'
            }}>
              <div style={{
                width: `${(enabledCount / FEATURE_TOGGLES.length) * 100}%`,
                height: '8px',
                backgroundColor: 'var(--color-primary, #2e7d32)',
                borderRadius: '6px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          <div className="card" style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-brand-text)' }}>
              How It Works
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '18px',
              color: 'var(--color-text-light)',
              lineHeight: '1.8',
              fontSize: '14px'
            }}>
              <li><strong>Enabled:</strong> Feature is visible and functional</li>
              <li><strong>Disabled:</strong> Feature is hidden from all menus</li>
              <li>Changes apply after page refresh</li>
              <li>Does not delete data - just hides the UI</li>
              <li>Reset to restore all defaults</li>
            </ul>
          </div>

          <div className="card" style={{ padding: '20px', marginTop: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-brand-text)' }}>
              Feature Details
            </h3>
            <div style={{ fontSize: '13px', color: 'var(--color-text-light)', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>eBay Integration:</strong> Includes listing generator and auction winner form
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>HOA/Tax Tracking:</strong> Adds extra fields to properties and payment breakdowns
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>Property Sources:</strong> Internal tool for tracking where you buy properties
              </p>
              <p style={{ margin: 0 }}>
                <strong>Sold Showcase:</strong> Public page showing your successful sales
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureSettings;
